import React, { useEffect, useState, useRef, useCallback } from "react";
import moment from "moment";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import "../../assets/css/nlis.css";
import { Button, Form } from "react-bootstrap";
import classNames from "classnames";
import CustomAlert from "../general/customAlert";
import CustomTable from "../general/CustomTable";
import DatePicker from "react-datepicker";
import { fetchProcessedBodies } from "../../api/processedBodies";
import SummaryBox from "./summaryBox";
import { killRegister, updateKillRegisterBody } from "../../api/nlis";
import { useCompany } from "../../helpers/CompanyContext";
import CircularProgress from "@mui/material/CircularProgress";

const KILL_REGISTER_COLUMNS = [
  { name: "Body No",         value: "body_number" },
  { name: "RFID",            value: "rfid_no" },
  { name: "NLIS_ID",         value: "nlis_id" },
  { name: "PIC",             value: "pic" },
  { name: "Customer",        value: "owner" },
  { name: "Origin",          value: "origin" },
  { name: "Production Date", value: "production_date" },
  { name: "Species",         value: "species" },
];

const SPECIES_CONFIG = {
  Cattle: { tableName: "beef", speciesValues: ["BOVINE"] },
  Sheep:  { tableName: "lamb", speciesValues: ["OVINE"] },
  Goat:   { tableName: "goat", speciesValues: ["CAPRINE", "GOAT"] },
};

const TABLES_WITH_RFID = ["beef", "goat"];

// Header aliases — keys are matched AFTER normalizing (lowercase, BOM stripped,
// non-alphanumerics collapsed to "_"), so "RFID", "rfid no", "Body No" all resolve.
const IMPORT_HEADER_ALIASES = {
  body_number: "body_number",
  body_no:     "body_number",
  bodyno:      "body_number",
  body:        "body_number",
  rfid_no:     "rfid_no",
  rfid_number: "rfid_no",
  rfid:        "rfid_no",
  nlis_id:     "nlis_id",
  nlisid:      "nlis_id",
  nlis:        "nlis_id",
  nlis_number: "nlis_id",
};

const normalizeHeader = (h) => {
  const key = h
    .toString()
    .replace(/^\ufeff/, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return IMPORT_HEADER_ALIASES[key] || key;
};

const KillRegisterPage = () => {
  const { selectedCompany } = useCompany();
  const isInitialMount     = useRef(true);
  const csvInputRef        = useRef(null);
  const abortControllerRef = useRef(null);

  const [selectedSpeciesLabel, setSelectedSpeciesLabel] = useState("Cattle");
  const [showSummaryModal, setShowSummaryModal]         = useState(false);
  const [summaryModalData, setSummaryModalData]         = useState([]);
  const [selectedBodyNos, setSelectedBodyNos]           = useState([]);
  const [processedBodies, setProcessedBodies]           = useState([]);
  const [filteredBodies, setFilteredBodies]             = useState([]);
  const [isSendingRequest, setSendingRequest]           = useState(false);
  const [isMsg, setIsMsg]                               = useState(false);
  const [msg, setMsg]                                   = useState(null);
  const [isError, setError]                             = useState(false);
  const [currentPage, setCurrentPage]                   = useState(1);
  const [totalPages, setTotalPages]                     = useState(0);
  const [sortConfig, setSortConfig]                     = useState({ key: "body_number", direction: "asc" });
  const [visibleColumns, setVisibleColumns]             = useState([]);
  const [selectedDate, setSelectedDate]                 = useState(null);
  const [isSavingRow, setIsSavingRow]                   = useState(false);
  const [csvImportMsg, setCsvImportMsg]                 = useState(null);

  // Exception warning toast
  const [exceptionDialog, setExceptionDialog] = useState(null);

  const rowsPerPage    = 20;
  const sortingColumns = ["body_number", "hscw"];
  const startIndex     = (currentPage - 1) * rowsPerPage;
  const endIndex       = startIndex + rowsPerPage;

  const { tableName: currentTableName } = SPECIES_CONFIG[selectedSpeciesLabel] || SPECIES_CONFIG.Cattle;
  const supportsRfid = TABLES_WITH_RFID.includes(currentTableName);

  // Columns that become inputs when a row is being edited
  const editableCells = supportsRfid ? ["rfid_no", "nlis_id", "pic"] : ["pic"];

  const filterBySpecies = useCallback((bodies, speciesLabel) => {
    const { speciesValues } = SPECIES_CONFIG[speciesLabel] || SPECIES_CONFIG.Cattle;
    return bodies
      .filter((b) => speciesValues.includes(b.species))
      .sort((a, b) => a.body_number - b.body_number);
  }, []);

  const UseSetFilteredBodies = useCallback((data) => {
    const sorted = [...data].sort((a, b) => {
      if (sortConfig.key) {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === "asc" ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === "asc" ? 1 : -1;
      }
      return a.body_number - b.body_number;
    });
    setFilteredBodies(sorted);
    setTotalPages(Math.ceil(data.length / rowsPerPage));
  }, [sortConfig]);

  const fetchBodies = useCallback(async (date, speciesLabel) => {
    try {
      const data = await fetchProcessedBodies(date);
      const bodies = data.map((d) => ({
        ...d,
        fat_score: d["p2fat"] ? d["p2fat"] : parseFloat("0.00").toFixed(2),
      }));
      setProcessedBodies(bodies);
      const filtered = filterBySpecies(bodies, speciesLabel);
      UseSetFilteredBodies(filtered);

      if (filtered.length > 0 && visibleColumns.length < 1) {
        setVisibleColumns(
          KILL_REGISTER_COLUMNS.reduce((acc, item) => {
            acc[item.name] = true;
            return acc;
          }, {})
        );
      }
    } catch (err) {
      console.error("[NLIS] Error fetching processed bodies:", err);
    }
  }, [visibleColumns, UseSetFilteredBodies, filterBySpecies]);

  useEffect(() => {
    const today = moment().format("YYYY-MM-DD");
    setSelectedDate(today);
    if (selectedCompany) fetchBodies(today, "Cattle");
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    if (selectedCompany) {
      setProcessedBodies([]);
      setFilteredBodies([]);
      setSelectedBodyNos([]);
      setCurrentPage(1);
      if (selectedDate) fetchBodies(selectedDate, selectedSpeciesLabel);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedDate && selectedCompany) fetchBodies(selectedDate, selectedSpeciesLabel);
   // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedCompany]);

  const handleSpeciesChange = (e) => {
    const label = e.target.value;
    setSelectedSpeciesLabel(label);
    setSelectedBodyNos([]);
    setCurrentPage(1);
    UseSetFilteredBodies(filterBySpecies(processedBodies, label));
  };

  const handleSort = useCallback((col) => {
    let direction = sortConfig.direction;
    let column = col;
    if (column) {
      if (sortConfig.key === column && sortConfig.direction === "asc") direction = "desc";
      else if (sortConfig.key === column && sortConfig.direction === "desc") direction = "asc";
      setSortConfig({ key: direction ? column : null, direction });
    } else { column = sortConfig.key; }
    setFilteredBodies((prev) =>
      [...prev].sort((a, b) => {
        if (a[column] < b[column]) return direction === "asc" ? -1 : 1;
        if (a[column] > b[column]) return direction === "asc" ? 1 : -1;
        return 0;
      })
    );
  }, [sortConfig]);

  const handleDateChange = (date) => {
    if (date === null) { setSelectedDate(null); return; }
    setSelectedDate(moment(date).format("YYYY-MM-DD"));
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handleCancel = () => {
    abortControllerRef.current?.abort();
    setSendingRequest(false);
  };

  // ── Row-level save with exception check ──────────────────────────────────
  const handleRowSave = async (row, editValues) => {
    setIsSavingRow(true);
    try {
      const payload = {
        tableName:      currentTableName,
        bodyNumber:     row.body_number,
        productionDate: moment(row.production_date).format("YYYY-MM-DD"),
      };

      if (supportsRfid) {
        if (editValues.rfid_no) payload.rfid   = editValues.rfid_no.trim();
        if (editValues.nlis_id) payload.nlisId = editValues.nlis_id.trim();
      }
      if (editValues.pic) payload.pic = editValues.pic.trim();

      await updateKillRegisterBody(payload);

      const updater = (rows) =>
        rows.map((r) =>
          r.body_number === row.body_number &&
          moment(r.production_date).format("YYYY-MM-DD") ===
            moment(row.production_date).format("YYYY-MM-DD")
            ? {
              ...r,
              rfid_no: supportsRfid ? (editValues.rfid_no?.trim() || r.rfid_no) : r.rfid_no,
              nlis_id: supportsRfid ? (editValues.nlis_id?.trim() || r.nlis_id) : r.nlis_id,
              pic:     editValues.pic?.trim() || r.pic,
            }
          : r
      );

      setProcessedBodies((prev) => updater(prev));
      setFilteredBodies((prev)  => updater(prev));

      setError(false);
      setIsMsg(true);
      setMsg("Body updated successfully.");
    } catch (err) {
      if (err?.response?.status === 409) {
        const data = err.response.data;
        setExceptionDialog({
          field:            data.field,
          value:            data.value,
          exception_status: data.exception_status,
          program_code:     data.program_code,
        });
      } else {
        setError(true);
        setIsMsg(true);
        setMsg(err?.response?.data?.message || err?.response?.data?.error || "Error saving changes.");
      }
    } finally {
      setIsSavingRow(false);
    }
  };

  // ── CSV / Excel Import ────────────────────────────────────────────────────
  const processImportRows = async (importRows) => {
    // Required: body_number + (rfid_no OR nlis_id). PIC and the other tag
    // field are optional — saved only when present. Rows with a body number
    // but no tag at all (missing tag) are skipped.
    const importMap = {};
    for (const row of importRows) {
      const bodyNo = row["body_number"]?.toString().trim();
      const rfid   = row["rfid_no"]?.toString().trim();
      const nlisId = row["nlis_id"]?.toString().trim();
      const pic    = row["pic"]?.toString().trim();
      if (bodyNo && (rfid || nlisId)) importMap[bodyNo] = { rfid, nlisId, pic };
    }

    if (Object.keys(importMap).length === 0) {
      const detected = importRows.length
        ? [...new Set(importRows.flatMap((r) => Object.keys(r)))].join(", ")
        : "none";
      console.warn("[Import] No usable rows. Parsed rows:", importRows);
      setCsvImportMsg(
        `File has no usable rows — each row needs a body number plus an RFID or NLIS ID. Columns detected: ${detected}.`
      );
      return;
    }

    const matchedRows = filteredBodies.filter(
      (r) => importMap[r.body_number?.toString()] !== undefined
    );
    const noMatchCount = filteredBodies.length - matchedRows.length;

    if (matchedRows.length === 0) {
      setCsvImportMsg("No body numbers in the file matched today's kill register.");
      return;
    }

    setCsvImportMsg(`Saving ${matchedRows.length} row${matchedRows.length !== 1 ? "s" : ""}...`);

    let savedCount = 0;
    let errorCount = 0;

    await Promise.all(
      matchedRows.map(async (r) => {
        const rowData = importMap[r.body_number?.toString()];
        try {
          const payload = {
            tableName:      currentTableName,
            bodyNumber:     r.body_number,
            productionDate: moment(r.production_date).format("YYYY-MM-DD"),
          };
          if (supportsRfid) {
            if (rowData.rfid)   payload.rfid   = rowData.rfid;
            if (rowData.nlisId) payload.nlisId = rowData.nlisId;
          }
          if (rowData.pic) payload.pic = rowData.pic;

          await updateKillRegisterBody(payload);
          savedCount++;
        } catch (err) {
          console.error(`[Import] Failed to save body ${r.body_number}:`, err);
          errorCount++;
        }
      })
    );

    const updater = (rows) =>
      rows.map((r) => {
        const rowData = importMap[r.body_number?.toString()];
        if (!rowData) return r;
        return {
          ...r,
          rfid_no: supportsRfid && rowData.rfid   ? rowData.rfid   : r.rfid_no,
          nlis_id: supportsRfid && rowData.nlisId ? rowData.nlisId : r.nlis_id,
          pic:     rowData.pic ? rowData.pic : r.pic,
        };
      });

    setProcessedBodies((prev) => updater(prev));
    setFilteredBodies((prev)  => updater(prev));

    const resultMsg = `Imported: ${savedCount} row${savedCount !== 1 ? "s" : ""} saved` +
      (errorCount   > 0 ? `, ${errorCount} failed`        : "") +
      (noMatchCount > 0 ? `, ${noMatchCount} not matched` : "") + ".";
    setCsvImportMsg(resultMsg);
  };

  const handleCsvImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = "";

    const ext = file.name.split(".").pop().toLowerCase();

    if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const wb = XLSX.read(ev.target.result, { type: "array" });

          // Use the first sheet that actually contains rows — workbooks
          // sometimes have an empty first sheet.
          let raw = [];
          for (const name of wb.SheetNames) {
            const json = XLSX.utils.sheet_to_json(wb.Sheets[name], {
              defval: "",
              raw:    false, // formatted strings, not raw cell values
            });
            if (json.length > 0) { raw = json; break; }
          }

          const rows = raw.map((r) =>
            Object.fromEntries(
              Object.entries(r).map(([k, v]) => [normalizeHeader(k), v])
            )
          );

          console.log("[Import] Excel parsed rows:", rows);
          processImportRows(rows);
        } catch (err) {
          console.error("[Import] Failed to read Excel file:", err);
          setCsvImportMsg("Failed to read Excel file. Please check the file format.");
        }
      };
      reader.onerror = () => setCsvImportMsg("Failed to read Excel file. Please check the file format.");
      reader.readAsArrayBuffer(file);
    } else {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: normalizeHeader,
        complete: (results) => processImportRows(results.data),
        error: () => setCsvImportMsg("Failed to read CSV file. Please check the file format."),
      });
    }
  };

// ── Upload SOAP ───────────────────────────────────────────────────────────
  const sendSOAPRequest = async () => {
    if (selectedBodyNos.length < 1) {
      setError(true); setIsMsg(true);
      setMsg("Please select at least one body to upload.");
      return;
    }

    // PIC is mandatory; RFID/NLIS ID are optional (manual kill upload allowed).
    const invalidBodies = selectedBodyNos.filter((body) => {
      const hasPic = body.pic && body.pic !== "0";
      return !hasPic;
    });

    if (invalidBodies.length > 0) {
      const bodyNos = invalidBodies.map((b) => b.body_number).join(", ");
      setError(true); setIsMsg(true);
      setMsg(
        `Cannot upload — Body No ${bodyNos} ${invalidBodies.length === 1 ? "is" : "are"} missing PIC. Please fill in the PIC before uploading.`
      );
      return;
    }

    if (!selectedDate) {
      setError(true); setIsMsg(true);
      setMsg("Please select a production date.");
      return;
    }

    abortControllerRef.current = new AbortController();
    setSendingRequest(true);
    setError(false);
    setIsMsg(false);

    try {
      const res = await killRegister({
        date:      selectedDate,
        tableName: currentTableName,
        bodies:    selectedBodyNos.map((body) => ({
          bodyNo:  body.body_number,
          species: selectedSpeciesLabel,
          rfid:    body.rfid_no?.trim() || "",
          nlisId:  body.nlis_id?.trim() || "",
          pic:     body.pic.trim(),
        })),
      }, abortControllerRef.current.signal);

      if (res.data?.data && res.data.data.length > 0) {
        setShowSummaryModal(true);
        setSummaryModalData(res.data.data);

        const updatedData = processedBodies.map((row) => {
          const match = res.data.data.find((r) => parseInt(r.body_no) === row.body_number);
          if (match && match.status?.toLowerCase() === "success") {
            return { ...row, upload_time: true };
          }
          return row;
        });

        UseSetFilteredBodies(filterBySpecies(updatedData, selectedSpeciesLabel));
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      const errorMsg = err?.response?.data?.message || err?.response?.data?.error || "Error in uploading...";      console.error("[NLIS] Kill upload error:", errorMsg);
      setError(true); setIsMsg(true); setMsg(errorMsg);
    } finally {
      setSendingRequest(false);
      setSelectedBodyNos([]);
    }
  };

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <div className="nlis-page kill-register-page">
      <h4 className="mb-3 text-center text-acumen-color">NLIS Kill Register</h4>

      <div className="container-fluid">
        <div className="d-flex flex-column flex-md-row">
          <div className="col-12 col-md-6 mb-1 mb-md-0 d-flex p-0">
            <Form.Group className="m-0 p-0 d-flex flex-column col-4 date-picker">
              <Form.Label className="m-0 ps-4">Production Date</Form.Label>
              <DatePicker
                selected={selectedDate ? new Date(selectedDate) : null}
                onChange={handleDateChange}
                dateFormat="yyyy/MM/dd"
                className="form-control w-100"
                id="production_date"
                name="production_date"
                disabled={isSendingRequest}
                onKeyDown={(e) => e.preventDefault()}
                isClearable={true}
                maxDate={new Date()}
              />
            </Form.Group>
            <Form.Group className="m-0 p-0 col-3 ms-5">
              <Form.Label className="m-0">Select Species</Form.Label>
              <select
                className="form-select m-0 w-100"
                style={{ height: "35px" }}
                disabled={isSendingRequest}
                value={selectedSpeciesLabel}
                onChange={handleSpeciesChange}>
                <option value="Cattle">Cattle</option>
                <option value="Sheep">Sheep</option>
                <option value="Goat">Goat</option>
              </select>
            </Form.Group>
          </div>

          <div className="col-12 col-md-4 d-flex justify-content-md-end mt-3 mt-md-0 align-items-center ms-auto">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              ref={csvInputRef}
              style={{ display: "none" }}
              onChange={handleCsvImport}
            />
            {supportsRfid && (
              <Button
                variant="outline-secondary"
                className="ms-2"
                disabled={isSendingRequest || !filteredBodies.length}
                onClick={() => csvInputRef.current?.click()}>
                Import CSV/Excel
              </Button>
            )}
            <Button
              className={classNames("btn btn-acumen-color ms-2", {
                disabled: isSendingRequest || selectedBodyNos.length < 1,
              })}
              onClick={sendSOAPRequest}>
              {isSendingRequest ? "Uploading..." : "Upload Bodies"}
            </Button>
          </div>
        </div>

        {isSendingRequest && (
          <div className="alert alert-info mt-4 d-flex align-items-center gap-3">
            <CircularProgress size={20} />
            <span>Uploading to NLIS — this may take up to 2 minutes...</span>
            <Button
              variant="outline-danger"
              size="sm"
              className="ms-auto"
              onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}

        {!isSendingRequest && (
          <div className="mt-4">
            {filteredBodies.length > 0 ? (
              <>
                <CustomTable
                  items={filteredBodies.slice(startIndex, endIndex)}
                  allItems={filteredBodies}
                  columns={KILL_REGISTER_COLUMNS}
                  visibleColumns={visibleColumns}
                  setVisibleColumns={setVisibleColumns}
                  horizontalMargin="mx-1"
                  cantHideColumns={["hscw"]}
                  maxHeight="calc(100vh - 320px)"
                  handleSort={handleSort}
                  sortConfig={sortConfig}
                  sortingColumns={sortingColumns}
                  isSelectable={true}
                  singleSelect={false}
                  selectableIDs={["body_number", "rfid_no", "nlis_id", "pic"]}
                  uniqueRowKey="body_number"
                  selectedRows={selectedBodyNos}
                  setSelectedRows={setSelectedBodyNos}
                  disabledKey={"upload_time"}
                  editableCells={editableCells}
                  onRowSave={handleRowSave}
                  isSavingRow={isSavingRow}
                />

                {totalPages > 1 && (
                  <div className="pagination mt-2">
                    <button className="btn btn-acumen-color text-white me-2" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button key={index + 1} className={`btn ${currentPage === index + 1 ? "btn-secondary" : "btn-light"} me-1`} onClick={() => handlePageChange(index + 1)}>{index + 1}</button>
                    ))}
                    <button className="btn btn-acumen-color text-white ms-2" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                  </div>
                )}
              </>
            ) : (
              !isError && <div className="alert alert-warning mt-2">No data found.</div>
            )}
          </div>
        )}
      </div>

      {/* ── Exception Warning Toast ────────────────────────────────────────── */}
      {exceptionDialog && (
        <div style={{
          position:        "fixed",
          bottom:          24,
          left:            "50%",
          transform:       "translateX(-50%)",
          zIndex:          9999,
          width:           340,
          backgroundColor: "#fff3cd",
          border:          "1px solid #ffc107",
          borderRadius:    8,
          boxShadow:       "0 4px 12px rgba(0,0,0,0.15)",
          padding:         "12px 16px",
        }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong style={{ color: "#856404" }}>⚠️ NLIS Exception Warning</strong>
            <button className="btn-close btn-sm" onClick={() => setExceptionDialog(null)} />
          </div>
          <p style={{ fontSize: 13, marginBottom: 8 }}>
            <strong>{exceptionDialog.field}</strong> value{" "}
            <code>{exceptionDialog.value}</code> is on the NLIS exception list and cannot be saved.
          </p>
          <div style={{ fontSize: 12, color: "#555" }}>
            Status: <strong>{exceptionDialog.exception_status || "—"}</strong> &nbsp;|&nbsp;
            Program: <strong>{exceptionDialog.program_code || "—"}</strong>
          </div>
        </div>
      )}

      {csvImportMsg && (
        <CustomAlert isError={false} message={csvImportMsg} showMessage={!!csvImportMsg} setShowMessage={(v) => { if (!v) setCsvImportMsg(null); }} />
      )}

      {isMsg && (
        <CustomAlert isError={isError} message={msg} showMessage={isMsg} setShowMessage={setIsMsg} />
      )}

      {showSummaryModal && (
        <SummaryBox message={summaryModalData} show={showSummaryModal} onClose={() => setShowSummaryModal(false)} />
      )}
    </div>
  );
};

export default KillRegisterPage;